import { useState, useContext, useEffect } from "react";
import {
  Dialog,
  DialogContent,
  DialogActions,
  Box,
  Typography,
  IconButton,
  TextField,
  Button,
  Stack,
  Alert,
  CircularProgress,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import SmartToyIcon from "@mui/icons-material/SmartToy";
import DescriptionIcon from "@mui/icons-material/Description";
import PsychologyIcon from "@mui/icons-material/Psychology";
import TonalityIcon from "@mui/icons-material/Tonality";
import SentimentSatisfiedAltIcon from "@mui/icons-material/SentimentSatisfiedAlt";
import WorkOutlineIcon from "@mui/icons-material/WorkOutline";
import BoltIcon from "@mui/icons-material/Bolt";
import EmojiEmotionsIcon from "@mui/icons-material/EmojiEmotions";
import VolunteerActivismIcon from "@mui/icons-material/VolunteerActivism";
import FavoriteBorderIcon from "@mui/icons-material/FavoriteBorder";
import ScienceIcon from "@mui/icons-material/Science";
import { collection, addDoc } from "firebase/firestore";
import { db } from "../../firebase";
import { LanguageContext } from "../../context/LanguageContext";
import { fetchContext } from "../../api/webchat";

export default function AgentCreateWizardMobile({ open, onClose, tenantId }) {
  const { translate } = useContext(LanguageContext);
  const [step, setStep] = useState(0);
  const [name, setName] = useState("");
  const [website, setWebsite] = useState("");
  const [guidelines, setGuidelines] = useState("");
  const [tone, setTone] = useState("");
  const [saving, setSaving] = useState(false);
  const [contextLoading, setContextLoading] = useState(false);
  const [contextError, setContextError] = useState("");
  const [contextInfo, setContextInfo] = useState("");
  const [lastFetchedUrl, setLastFetchedUrl] = useState("");

  const toneOptions = [
    { value: "friendly", labelKey: "ToneFriendly", Icon: SentimentSatisfiedAltIcon, color: "#00E676" },
    { value: "professional", labelKey: "ToneProfessional", Icon: WorkOutlineIcon, color: "#64B5F6" },
    { value: "concise", labelKey: "ToneConcise", Icon: BoltIcon, color: "#FFD54F" },
    { value: "playful", labelKey: "TonePlayful", Icon: EmojiEmotionsIcon, color: "#FF80AB" },
    { value: "supportive", labelKey: "ToneSupportive", Icon: VolunteerActivismIcon, color: "#BA68C8" },
    { value: "empathetic", labelKey: "ToneEmpathetic", Icon: FavoriteBorderIcon, color: "#FF8A65" },
    { value: "technical", labelKey: "ToneTechnical", Icon: ScienceIcon, color: "#26C6DA" },
    { value: "neutral", labelKey: "ToneNeutral", Icon: TonalityIcon, color: "#90A4AE" },
  ];

  const totalSteps = 4;

  const canNext = () => {
    if (step === 0) return name.trim().length > 0;
    if (step === 1) return isValidUrl(website) && !contextLoading;
    if (step === 2) return guidelines.trim().length > 0;
    if (step === 3) return !!tone;
    return true;
  };

  const reset = () => {
    setStep(0);
    setName("");
    setWebsite("");
    setGuidelines("");
    setTone("");
    setContextLoading(false);
    setContextError("");
    setContextInfo("");
    setLastFetchedUrl("");
  };

  const handleClose = () => {
    if (saving) return;
    reset();
    onClose?.();
  };

  const handleCreate = async () => {
    if (!tenantId || saving) return;
    setSaving(true);
    try {
      await addDoc(collection(db, "tenants", tenantId, "agents"), {
        name: name.trim(),
        website: website.trim(),
        description: "",
        guidelines: guidelines.trim(),
        tone: tone,
        publicEnabled: true,
        createdAt: new Date(),
        updatedAt: new Date(),
      });
      handleClose();
    } catch (e) {
      setSaving(false);
    }
  };

  const handlePrefillContext = async (targetUrl) => {
    if (!tenantId || !targetUrl || contextLoading) return;
    setContextLoading(true);
    setContextError("");
    setContextInfo("");
    try {
      const resp = await fetchContext(tenantId, targetUrl);
      const ctx = resp?.contextText || "";
      if (ctx) {
        setGuidelines(ctx);
        setContextInfo("Contexte pré-rempli depuis le site.");
      } else {
        setContextInfo("Aucun contenu détecté, remplissez manuellement.");
      }
    } catch (e) {
      setContextError("Impossible de récupérer le contenu du site. Vérifiez l'URL ou réessayez.");
    } finally {
      setContextLoading(false);
    }
  };

  const isValidUrl = (value) => {
    try {
      const u = new URL(value);
      return u.protocol === "http:" || u.protocol === "https:";
    } catch {
      return false;
    }
  };

  useEffect(() => {
    if (!website.trim()) {
      setContextError("");
      setContextInfo("");
      return;
    }
    if (!isValidUrl(website.trim())) {
      setContextError("URL invalide (http ou https).");
      setContextInfo("");
      return;
    }
    setContextError("");
    if (website.trim() === lastFetchedUrl) return;
    setLastFetchedUrl(website.trim());
    handlePrefillContext(website.trim());
  }, [website, tenantId]);

  const StepShell = ({ icon: IconCmp, title, subtitle, children }) => (
    <Box sx={{ p: 2 }}>
      <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
        <IconCmp sx={{ color: "#00E676" }} />
        <Typography variant="h6" sx={{ fontWeight: 800 }}>
          {title}
        </Typography>
      </Box>
      {subtitle && (
        <Box
          sx={{
            mb: 2,
            p: 1.2,
            borderRadius: 1.5,
            bgcolor: "rgba(0,230,118,0.08)",
            border: "1px solid rgba(0,230,118,0.25)",
          }}
        >
          <Typography variant="body1" sx={{ lineHeight: 1.5 }}>
            {subtitle}
          </Typography>
        </Box>
      )}
      {children}
    </Box>
  );

  return (
    <Dialog open={open} onClose={handleClose} fullScreen PaperProps={{ sx: { overflow: "hidden" } }}>
      {/* top bar */}
      <Box sx={{ display: "flex", alignItems: "center", p: 1.5, borderBottom: 1, borderColor: "divider" }}>
        <Typography sx={{ flex: 1, textAlign: "center", fontWeight: 700 }}>
          {translate("CreateAgent") || "Create your agent"}
        </Typography>
        <IconButton onClick={handleClose} sx={{ position: "absolute", right: 8 }}>
          <CloseIcon />
        </IconButton>
      </Box>

      <DialogContent sx={{ p: 0, pb: 0 }} dividers>
        {step === 0 && (
          <StepShell
            icon={SmartToyIcon}
            title={(translate && translate("AgentName")) || "Agent name"}
            subtitle={(translate && translate("AgentNameHelp")) || "Give your assistant a short, memorable name. You can change it later."}
          >
            <TextField
              fullWidth
              autoFocus
              label={(translate && translate("AgentName")) || "Agent name"}
              value={name}
              onChange={(e) => setName(e.target.value)}
            />
          </StepShell>
        )}

        {step === 1 && (
          <StepShell
            icon={DescriptionIcon}
            title={translate("AgentWebsite") || "Website URL"}
            subtitle={
              translate("AgentWebsiteHelp") ||
              "Paste your site URL; we'll pull key info to prefill your agent automatically."
            }
          >
            <Stack spacing={2}>
              <TextField
                fullWidth
                autoFocus
                label={translate("AgentWebsite") || "Website URL"}
                placeholder="https://www.votresite.com"
                value={website}
                onChange={(e) => setWebsite(e.target.value)}
                error={!!contextError}
                helperText={contextError || ""}
              />
              {contextLoading && (
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <CircularProgress size={16} />
                  <Typography variant="body2">Analyse en cours...</Typography>
                </Box>
              )}
              {contextInfo && !contextError && <Alert severity="success">{contextInfo}</Alert>}
              {contextError && <Alert severity="error">{contextError}</Alert>}
            </Stack>
          </StepShell>
        )}

        {step === 2 && (
          <StepShell
            icon={PsychologyIcon}
            title={(translate && translate("AgentContext")) || "Context & persona"}
            subtitle={(translate && translate("AgentContextHelp")) || "Describe how your agent should speak, behave, and what it should focus on."}
          >
            <TextField
              fullWidth
              multiline
              minRows={6}
              label={(translate && translate("AgentContext")) || "Context & persona"}
              value={guidelines}
              onChange={(e) => setGuidelines(e.target.value)}
            />
          </StepShell>
        )}

        {step === 3 && (
          <StepShell
            icon={TonalityIcon}
            title={(translate && translate("AgentTone")) || "Tone"}
            subtitle={(translate && translate("AgentToneHelp")) || "Pick a tone that fits your brand. You can adjust later."}
          >
            <Stack direction="row" spacing={1} flexWrap="wrap" sx={{ gap: 1 }}>
              {toneOptions.map((opt) => (
                <Button
                  key={opt.value}
                  variant={tone === opt.value ? "contained" : "outlined"}
                  onClick={() => setTone(opt.value)}
                  startIcon={<opt.Icon />}
                  sx={{ textTransform: "none" }}
                >
                  {(translate && translate(opt.labelKey)) || opt.value}
                </Button>
              ))}
            </Stack>
          </StepShell>
        )}
      </DialogContent>

      <DialogActions sx={{ p: 2 }}>
        <Button onClick={handleClose} disabled={saving} sx={{ mr: "auto" }}>
          {translate("Cancel") || "Cancel"}
        </Button>
        {step > 0 && (
          <Button onClick={() => setStep((s) => s - 1)} disabled={saving} variant="outlined">
            {translate("Back") || "Back"}
          </Button>
        )}
        {step < totalSteps - 1 ? (
          <Button onClick={() => setStep((s) => s + 1)} disabled={!canNext() || saving} variant="contained">
            {translate("Next") || "Next"}
          </Button>
        ) : (
          <Button onClick={handleCreate} disabled={!canNext() || saving} variant="contained">
            {translate("Create") || "Create"}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}
